import React, { useCallback, useEffect, useRef } from "react";
import { CustomModal, FormCheck, FormEntry, FormTextArea } from "components";
import {} from "react-router-dom";
import { FormSelect } from "components";
import { centreOptions } from "constants";
import { useNotificationContext } from "contexts";
import { CustomAlert } from "components";
import { useMutation } from "react-query";
import { Button } from "reactstrap";
import { useMemo } from "react";
import apiService from "services/api";
import { ProtectedImg } from "components";
import { ImageModal } from "components";
import { capitalize } from "utils/capitalize";
import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { encodeImageFileAsURL } from "utils/fileToBase64";
import { getDataURIMimeType, mimeTypes } from "utils/getDataURIMimeType";
import { openBase64InNewTab } from "utils/openBase64InNewTab";
import { FiCheck, FiX } from "react-icons/fi";
import { useSearchParams } from "hooks";

const initialImageModalState = {
  open: false,
  src: null,
};

export const ViewClaimsModal = ({
  state,
  hideModal,
  showModal,
  editAction,
  approveActions,
  refetch,
  deleteAction,
  payoutAction,
}) => {
  const approvalImagesInputRef = useRef(null);
  const receiptImageInputRef = useRef(null);
  const { createNotification } = useNotificationContext();
  const [editable, setEditable] = useState();
  const deleteSubmit = useMutation({
    mutationFn: () => apiService.staff.claims.delete(state?.data?.id),
    onSuccess: () => {
      createNotification({
        type: "success",
        message: "Deleted claim successfully",
      });
      refetch();
      setDisplayDeleteAlert(false);
      hideModal();
    },
  });
  const [displayPayoutAlert, setDisplayPayoutAlert] = useState(false);
  const payoutSubmit = useMutation({
    mutationFn: () => apiService.staff.claims.payout(state?.data?.id),
    onSuccess: () => {
      createNotification({
        type: "success",
        message: "Payout claim successfully!",
      });
      refetch();
      setDisplayPayoutAlert(false);
      hideModal();
    },
  });
  const approveAction = useMutation({
    mutationFn: (data) =>
      apiService.staff.claims.approve(state?.data?.id, data),
    onSuccess: () => {
      createNotification({
        type: "success",
        message: "Approved/Dissaproved claim successfully",
      });
      hideAlert();
      hideModal();
      refetch();
    },
  });
  const editSubmit = useMutation({
    mutationFn: (data) => {
      const res = apiService.staff.claims.update(state?.data?.id, data);
      return res;
    },
    onSuccess: () => {
      createNotification({
        type: "success",
        message: "Edit claim successfully",
      });
      hideAlert();
      hideModal();
      setDisplayEditAlert(false);
      refetch();
    },
  });
  const [imageModalState, setImageModalState] = useState(
    initialImageModalState,
  );
  const [displayDeleteAlert, setDisplayDeleteAlert] = useState(false);
  const [displayAlert, setDisplayAlert] = useState("");
  const [displayEditAlert, setDisplayEditAlert] = useState(false);

  const {
    reset: editReset,
    register: editRegister,
    formState: { errors: editErrors },
    control,
    watch: editWatch,
    clearErrors,
    getValues: getEditValues,
    setValue: setEditValue,
    trigger: editTrigger,
    setError: setEditError,
    handleSubmit: editHandleSubmit,
  } = useForm();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      isApprove: undefined,
      remark: "",
    },
  });

  const hideAlert = () => {
    reset();
    setDisplayAlert("");
  };
  const showAlert = (alertType) => {
    setValue("isApprove", alertType === "approve");
    setDisplayAlert(alertType);
  };

  const {
    fields: approvalImgPaths,
    append: appendApprovalImgPaths,
    remove: removeApprovalImgPaths,
  } = useFieldArray({
    control,
    name: "approvalImgPaths",
  });
  const {
    fields: receiptImgPaths,
    append: appendReceiptImgPaths,
    remove: removeReceiptImgPaths,
  } = useFieldArray({
    control,
    name: "receiptImgPaths",
  });

  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    const claimId = searchParams.get("claimIdExpanded");
    if (claimId) {
      apiService.staff.claims.get(claimId).then((data) => showModal(data));
    }
  }, [searchParams, showModal]);

  const isAdvancePayment = editWatch("isAdvancePayment");
  const claimPeriod = editWatch("claimPeriod");

  const onApprovalFileChange = (type) => async (e) => {
    if (!e.target.files.length) return;
    clearErrors(type === "approval" ? "approvalImgPaths" : "receiptImgPaths");
    const imgURI = await encodeImageFileAsURL(e.target.files[0]);
    if (type === "approval")
      appendApprovalImgPaths({ src: imgURI }); // use src json object because appending normal string makes the whole object spreaded
    else appendReceiptImgPaths({ src: imgURI });
    e.target.value = "";
  };

  const resetData = useCallback(() => {
    if (!state?.data) return;
    editReset({ ...state.data, approvalImgPaths: [], receiptImgPaths: [] });
    state?.data?.approvalImgPaths?.forEach((imgP) =>
      appendApprovalImgPaths({ src: imgP }),
    );
    state?.data?.receiptImgPaths?.forEach((imgP) =>
      appendReceiptImgPaths({ src: imgP }),
    );
  }, [appendApprovalImgPaths, appendReceiptImgPaths, editReset, state?.data]);

  React.useEffect(() => {
    resetData();
  }, [resetData, state?.data]);

  React.useEffect(() => {
    if (!state.open) {
      setEditable(false);
      editReset();
      reset();
      setSearchParams({});
    }
  }, [editReset, reset, setSearchParams, state.open]);

  const status = editWatch("status");
  const isPettyCash = editWatch("isPettyCash");

  const isApprovedStatus = React.useMemo(() => status === "approved", [status]);

  const isAdvancePaymentEditable = React.useMemo(() => {
    return state?.data?.isAdvancePayment;
  }, [state?.data]);

  const isEditable = React.useMemo(() => {
    return status === "pending" || status === "rejected";
  }, [status]);

  const isFieldsEditable = React.useMemo(() => {
    return editable && isEditable;
  }, [editable, isEditable]);

  return (
    <>
      <CustomAlert
        show={Boolean(displayAlert)}
        title={`${capitalize(displayAlert)} Confirmation`}
        submitButtonText="Confirm"
        hideModal={hideAlert}
        submitModal={() => {
          handleSubmit((data) => approveAction.mutate(data))();
        }}
        body={useMemo(() => {
          switch (displayAlert) {
            case "disapprove": {
              return (
                <FormEntry
                  formLabel="Dissaprove reason"
                  type="text"
                  className="form-control"
                  placeholder="Leave blank if empty"
                  register={register}
                  name="remark"
                />
              );
            }
            case "approve": {
              return <b className="text-warning">Confirm approve claim?</b>;
            }

            default:
              return;
          }
        }, [approveAction, displayAlert, handleSubmit, register])}
      />

      <CustomAlert
        show={displayPayoutAlert}
        title="Payout Confirmation"
        submitButtonText="Confirm"
        hideModal={() => setDisplayPayoutAlert(false)}
        submitModal={() => {
          handleSubmit((data) => payoutSubmit.mutate(data))();
        }}
        body={<div className="text-white">Confirm payout to staff?</div>}
      />

      <CustomAlert
        show={displayDeleteAlert}
        title="Delete Confirmation"
        submitButtonText="Confirm"
        hideModal={() => setDisplayDeleteAlert(false)}
        submitModal={() => deleteSubmit.mutate(state?.data?.id)}
      />
      <CustomAlert
        show={displayEditAlert}
        title="Edit Confirmation"
        submitButtonText="Confirm"
        hideModal={() => setDisplayEditAlert(false)}
        disabled={editSubmit.isLoading}
        submitModal={() => {
          editHandleSubmit((data) => {
            if (data.isAdvancePayment && isApprovedStatus) {
              if (!data.receiptImgPaths?.length)
                return setEditError("receiptImgPaths", [
                  { message: "Receipt image is required", type: "onChange" },
                ]);
            }
            editSubmit.mutate({
              ...data,
              receiptImgPaths: data.receiptImgPaths?.map(
                (resource) => resource.src,
              ),
              approvalImgPaths: data.approvalImgPaths?.map(
                (resource) => resource.src,
              ),
            });
          })();
        }}
      />

      <CustomModal
        show={state.open}
        hideModal={hideModal}
        title={editable ? "Edit Claim" : "View Claim"}
        buttons={
          <>
            {payoutAction &&
              !editable &&
              state?.data?.status === "approved" && (
                <Button
                  color="info"
                  type="button"
                  onClick={() => setDisplayPayoutAlert(true)}
                >
                  Payout
                </Button>
              )}
            {approveActions && state?.data?.status !== "paid" && (
              <>
                <Button onClick={() => showAlert("approve")} color="primary">
                  Approve
                </Button>
                <Button onClick={() => showAlert("disapprove")} color="light">
                  Disapprove
                </Button>
              </>
            )}
            {editAction &&
              (isEditable || isAdvancePaymentEditable) &&
              (!editable ? (
                <Button
                  color="success"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setEditable(true);
                  }}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    color="success"
                    type="button"
                    onClick={() => {
                      editHandleSubmit(() => {
                        setDisplayEditAlert(true);
                      })();
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    color="secondary"
                    type="button"
                    onClick={() => {
                      resetData();
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    color="light"
                    type="button"
                    onClick={() => {
                      resetData();
                      setEditable(false);
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ))}
            {deleteAction && !editable && state?.data?.status === "pending" && (
              <Button
                color="danger"
                type="button"
                onClick={() => setDisplayDeleteAlert(true)}
              >
                Delete
              </Button>
            )}
          </>
        }
      >
        <div className="row text-white text-left">
          <div className="col-12 col-md-6 mb-2">
            <FormEntry
              formLabel="App. Date"
              register={editRegister}
              name="createdAt"
              type="text"
              required={true}
              readOnly={true}
            />
          </div>
          <div className="col-12 col-md-6 mb-2">
            <FormEntry
              formLabel="Employee Name"
              register={editRegister}
              name="staff.fullName"
              type="text"
              required={true}
              readOnly={true}
            />
          </div>
          <div className="col-12 col-md-6 mb-2">
            <FormEntry
              formLabel="Claim ID"
              register={editRegister}
              name="id"
              type="text"
              required={true}
              readOnly={true}
            />
          </div>
          <div className="col-12 col-md-6 mb-2">
            <FormSelect
              formLabel="Claim Type"
              register={editRegister}
              name="isPettyCash"
              required={true}
              options={[
                { label: "Petty Cash", value: true },
                { label: "Cheque", value: false },
              ]}
              readOnly={!isEditable || !editable}
            />
          </div>
          {!isPettyCash && (
            <div className="col-12 col-md-6 mb-2">
              <FormEntry
                formLabel="Cheque To"
                register={() =>
                  editRegister("chequeToFullName", {
                    validate: {
                      minValue: (v) => v.length >= 0,
                    },
                    required: true,
                  })
                }
                name="chequeToFullName"
                placeholder="Cheque Receiver Full Name"
                error={errors?.chequeToFullName}
                required={!isPettyCash}
                readOnly={!isEditable || !editable}
              />
            </div>
          )}
          <div className="col-12 col-md-6 mb-2">
            <FormSelect
              formLabel="Company"
              register={editRegister}
              name="company"
              options={[
                { value: "G", label: "Genius" },
                { value: "S", label: "Scientech" },
              ]}
              readOnly={!isEditable || !editable}
              error={errors?.company}
              required={true}
            />
          </div>
          <div className="col-12 col-md-6 mb-2">
            <FormEntry
              formLabel="Claim Period"
              register={editRegister}
              name="claimPeriod"
              type="text"
              readOnly={true}
              errors={editErrors?.claimPeriod}
            />
          </div>
          <div className="col-12 col-md-6 mb-2">
            {!claimPeriod ? (
              <FormEntry
                formLabel="Invoice Date"
                register={editRegister}
                name="date"
                type="text"
                readOnly={true}
                errors={editErrors?.claimPeriod}
              />
            ) : null}
          </div>
          <div className="col-12 col-md-6 mb-2">
            <FormEntry
              formLabel="Status"
              register={editRegister}
              name="status"
              type="text"
              required={true}
              readOnly={true}
            />
          </div>
          <div className="col-12 col-md-6 mb-2">
            {isEditable && isAdvancePaymentEditable ? (
              <FormCheck
                register={editRegister}
                name="isAdvancePayment"
                formLabel={<div className="text-primary">Is advance?</div>}
              />
            ) : (
              <div className="mt-2">
                Is advance?{" "}
                {isAdvancePayment ? (
                  <FiCheck className="text-success" />
                ) : (
                  <FiX className="text-danger" />
                )}
              </div>
            )}
          </div>
          {isAdvancePayment && (
            <div className="col-12 col-md-6 mb-2">
              <FormEntry
                formLabel="Actual Claim Amount (HKD)"
                type="number"
                error={editErrors?.advancePaymentActualAmount}
                register={() =>
                  editRegister("advancePaymentActualAmount", {
                    required: isApprovedStatus,
                    valueAsNumber: true,
                  })
                }
                readOnly={!editable}
                placeholder="Total amount of claim"
              />
            </div>
          )}
          <div className="col-12 col-md-6 mb-2">
            <FormEntry
              formLabel="Item Name"
              register={editRegister}
              name="itemName"
              type="text"
              required={true}
              readOnly={!editable || !isEditable}
              errors={editErrors?.itemName}
            />
          </div>
          <div className="col-12 col-md-6 mb-2">
            <FormTextArea
              formLabel="Reason"
              register={editRegister}
              name="reason"
              type="text"
              required={true}
              errors={editErrors?.reason}
              readOnly={!editable}
            />
          </div>
          <div className="col-12 col-md-6 mb-2">
            <FormEntry
              formLabel="Claimable Amount"
              register={editRegister}
              name="price"
              type="number"
              required={true}
              errors={editErrors?.price}
              readOnly={!editable || isAdvancePaymentEditable}
            />
          </div>
          <div className="col-12 col-md-6 mb-2">
            <FormSelect
              formLabel="Centre"
              register={editRegister}
              name="centre"
              options={centreOptions}
              error={editErrors.centre}
              required={true}
              readOnly={!editable || isAdvancePaymentEditable}
            />
          </div>
          <div className="col-12 col-md-6 mb-2">
            <div className="font-weight-bold text-primary">
              Dissaprove Reason
            </div>
            <div>
              {state.data?.remark || <span className="text-muted">None</span>}
            </div>
          </div>
          <div className="col-12 col-md-6 mb-2">
            <div className="font-weight-bold text-primary">Reviewer</div>
            {state?.data?.approvedBy?.fullName ? (
              <div className="d-flex align-items-center">
                <span>{state?.data?.approvedBy?.fullName}</span>
                <a
                  className="btn btn-sm btn-primary ml-3"
                  href={`mailto:${state?.data?.approvedBy?.email}`}
                >
                  Contact
                </a>
              </div>
            ) : (
              "Not reviewed"
            )}
          </div>
          <div className="col-12 col-md-6 mb-2">
            <div className="font-weight-bold text-primary">Approval Images</div>
            {editable ? (
              <div
                className="px-3 py-2 pb-4 mb-3 rounded-lg"
                style={{ border: "1px solid #ADB5DB" }}
              >
                <input
                  type="file"
                  className="form-control d-none"
                  onChange={onApprovalFileChange("approval")}
                  accept="application/pdf, image/*"
                  ref={approvalImagesInputRef}
                />
                {editErrors?.approvalImgPaths?.[0] && (
                  <small className="text-danger">
                    {editErrors?.approvalImgPaths?.[0]?.message}
                  </small>
                )}
                <div className="mt-3">
                  <small
                    className="border border-light rounded px-3 py-2 text-light"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      approvalImagesInputRef.current?.click();
                    }}
                  >
                    Click to upload
                  </small>
                </div>

                {Boolean(approvalImgPaths.length) && (
                  <div className="row mt-3 ">
                    {approvalImgPaths.map((resource, index) => (
                      <div className="col-4" key={resource.id}>
                        <span
                          className="border border-danger"
                          style={{ cursor: "pointer", borderRadius: 5 }}
                          onClick={() => removeApprovalImgPaths(index)}
                        >
                          <FiX className="text-danger" />
                          <small className="text-danger mx-1">Remove</small>
                        </span>
                        <div className="mt-1">
                          <small>
                            {getDataURIMimeType(resource.src) !==
                            mimeTypes.pdf ? (
                              <ProtectedImg
                                src={resource.src}
                                className="img-fluid rounded text-white"
                                alt="Resource Uploaded"
                                onClick={() =>
                                  setImageModalState({
                                    open: true,
                                    src: resource.src,
                                  })
                                }
                              />
                            ) : (
                              <span
                                onClick={() => openBase64InNewTab(resource.src)}
                                style={{
                                  cursor: "pointer",
                                  borderRadius: 5,
                                  padding: 2,
                                }}
                                className="border border-success text-success px-1"
                              >
                                View Resource {index + 1}
                              </span>
                            )}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              state?.data?.approvalImgPaths?.map((src, index) => (
                <ProtectedImg
                  src={src}
                  key={index}
                  onClick={() => setImageModalState({ open: true, src })}
                  alt={`Approval img ${index}`}
                />
              ))
            )}
          </div>

          <div className="col-12 col-md-6 mb-2">
            <div className="font-weight-bold text-primary">Receipt Images</div>
            {editable ? (
              <div
                className="px-3 py-2 pb-4 mb-3 rounded-lg"
                style={{ border: "1px solid #ADB5DB" }}
              >
                <input
                  type="file"
                  className="form-control d-none"
                  onChange={onApprovalFileChange("receipt")}
                  accept="application/pdf, image/*"
                  ref={receiptImageInputRef}
                />
                {editErrors?.receiptImgPaths?.[0] && (
                  <small className="text-danger">
                    {editErrors?.receiptImgPaths?.[0]?.message}
                  </small>
                )}
                <div className="mt-3">
                  <small
                    className="border border-light rounded px-3 py-2 text-light"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      receiptImageInputRef.current?.click();
                    }}
                  >
                    Click to upload
                  </small>
                </div>

                {Boolean(receiptImgPaths.length) && (
                  <div className="row mt-3 ">
                    {receiptImgPaths.map((resource, index) => (
                      <div className="col-4" key={index}>
                        <span
                          className="border border-danger"
                          style={{ cursor: "pointer", borderRadius: 5 }}
                          onClick={() => removeReceiptImgPaths(index)}
                        >
                          <FiX className="text-danger" />
                          <small className="text-danger mx-1">Remove</small>
                        </span>
                        <div className="mt-1">
                          <small>
                            {getDataURIMimeType(resource.src) !==
                            mimeTypes.pdf ? (
                              <ProtectedImg
                                src={resource.src}
                                className="img-fluid rounded text-white"
                                alt="Resource Uploaded"
                                onClick={() =>
                                  setImageModalState({
                                    open: true,
                                    src: resource.src,
                                  })
                                }
                              />
                            ) : (
                              <span
                                onClick={() => openBase64InNewTab(resource.src)}
                                style={{
                                  cursor: "pointer",
                                  borderRadius: 5,
                                  padding: 2,
                                }}
                                className="border border-success text-success px-1"
                              >
                                View Resource {index + 1}
                              </span>
                            )}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              state?.data?.receiptImgPaths?.map((src, index) => (
                <ProtectedImg
                  src={src}
                  key={index}
                  onClick={() => setImageModalState({ open: true, src })}
                  alt={`Receipt img ${index}`}
                />
              ))
            )}
          </div>
        </div>
      </CustomModal>
      <ImageModal
        open={imageModalState.open}
        hideModal={() => setImageModalState(initialImageModalState)}
      >
        <ProtectedImg src={imageModalState.src} alt="" className="img-fluid" />
      </ImageModal>
    </>
  );
};
